<template>
  <SharedBaseVideoScrub
    v-if="!isInitialLoad && media"
    :src="media.video"
    :poster="media.poster"
    :progress="progress"
    smooth
    :canBeInitialized="props.canBeInitialized"
    :isDisabled="props.isDisabled"
    :serverFramesCount="media.serverFramesCount"
    @load="emit('load')" />
</template>

<script lang="ts" setup>
import { useRuntimeConfig } from '#app/nuxt';
import { useHead } from '#imports';
import { computed, onMounted, ref } from 'vue';
import { SharedBaseVideoScrub } from '@seb-platform/shared/components/base';
import { useBreakpoint } from '@seb-platform/shared/composables';
import { hasMp4ScrubSupport } from '@seb-platform/shared/utils/browser';
import { hasWebCodecsSupport, serverFramesCountMap } from '@seb-platform/shared/utils/video-scrolling';
import type { UtilVideoScrubStoryblok } from '~/types/storyblok-generated';

const props = withDefaults(
  defineProps<{
    blok: UtilVideoScrubStoryblok;
    progress: number;
    /**
     * Used to determine what video source to use.
     */
    mediaSource?: 'tablet' | 'desktop' | 'mobile';
    isDisabled?: boolean;
    canBeInitialized?: boolean;
    preloadPoster?: boolean;
  }>(),
  { progress: 0, mediaSource: 'desktop', canBeInitialized: true }
);

const emit = defineEmits<{ (e: 'load'): void }>();

const { state: stateBreakpoint } = useBreakpoint();
const config = useRuntimeConfig();

const isInitialLoad = ref(true);

const showTabletMedia = computed(() => {
  return props?.mediaSource !== undefined ? props.mediaSource === 'tablet' : stateBreakpoint.isTablet;
});
const showDesktopMedia = computed(() => {
  return props?.mediaSource !== undefined ? props.mediaSource === 'desktop' : stateBreakpoint.isLaptop;
});

const media = computed(() => {
  const useMp4 = hasMp4ScrubSupport() || hasWebCodecsSupport();

  let video: string | null | undefined;
  let poster: string | null | undefined;

  if (props.blok.type === 'image-sequence') {
    const variant = showDesktopMedia.value ? 'desktop' : showTabletMedia.value ? 'tablet' : 'mobile';

    const baseUrl = config.public.AMPLIFY_OUTPUTS.custom.publicAssetsBucket.baseUrl;
    let imageSequence = props.blok.image_sequence;
    if (
      config.public.IS_DEVELOPMENT &&
      imageSequence !== 'wmf_950-s-stage' &&
      imageSequence !== 'wmf_950-s-features-highlight'
    ) {
      // During local development, we force the image sequence to use local assets, so we don't rely on the public assets bucket
      console.warn('The UtilVideoScrub component is using local assets (`wmf_950-s-stage`) for the image sequence.');
      imageSequence = 'wmf_950-s-stage';
    }

    const url = `${baseUrl}/images/video-scrub/${imageSequence}_${variant}/frame-[[id]].webp`;

    let serverFramesCount = 0;
    if (imageSequence) {
      serverFramesCount = serverFramesCountMap[imageSequence]?.[variant];
    }
    if (!serverFramesCount) {
      throw new Error(`No server frames count found for image sequence "${imageSequence}" and variant "${variant}"`);
    }

    return {
      video: url,
      poster: null, // No poster for image sequence, as first frame is loaded instead
      serverFramesCount,
    };
  }

  if (showDesktopMedia.value) {
    poster = props.blok.video_poster?.filename;
    video = useMp4 ? props.blok.video?.filename : props.blok.video_webm?.filename;
  } else if (showTabletMedia.value) {
    // We fall back to desktop if no tablet media is provided to provide backwards compatibility
    video = useMp4
      ? // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        props.blok.video_tablet?.filename || props.blok.video?.filename
      : // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        props.blok.video_tablet_webm?.filename || props.blok.video_webm?.filename;
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    poster = props.blok.video_tablet_poster?.filename || props.blok.video_poster?.filename;
  } else {
    video = useMp4 ? props.blok.video_mobile?.filename : props.blok.video_mobile_webm?.filename;
    poster = props.blok.video_mobile_poster?.filename;
  }

  return { video, poster };
});

/**
 * Preload the poster image
 */
if (props.preloadPoster) {
  useHead({
    link: [
      {
        rel: 'preload',
        as: 'image',
        href: props.blok.video_poster?.filename,
      },
      {
        rel: 'preload',
        as: 'image',
        href: props.blok.video_tablet_poster?.filename,
      },
      {
        rel: 'preload',
        as: 'image',
        href: props.blok.video_mobile_poster?.filename,
      },
    ],
  });
}

onMounted(() => {
  isInitialLoad.value = false;
});
</script>
